import type { CharacterPortrait, Mission, TrustLabels } from '../types';

export type TrustPointsByCharacter = Record<string, number>;

export interface TrustCampaignStore {
  byMission: Record<string, TrustPointsByCharacter>;
}

export interface AllyTrustDisplayRow {
  characterId: string;
  name: string;
  givenName: string;
  role: string;
  portraitImage?: string;
  label: string;
  points: number;
  cranTier: 0 | 1 | 2 | 3;
}

export const CAMPAIGN_KEY_PREFIX = 'vn_trust_campaign_';
const ZERO_TRUST_LABEL = 'ne vous connaît pas';
const NON_ALLY_ID_RE = /^(ordinateur|telephone|hero_interior|hero)$/;
const NON_ALLY_NAME_RE = /ordinateur|t[ée]l[ée]phone|int[ée]rieur|^h[ée]ros$/i;

const FRENCH_COUNTS = ['', 'Une', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six', 'Sept', 'Huit', 'Neuf', 'Dix'];

export const DEFAULT_TRUST_LABELS: Record<'1' | '2' | '3', string> = {
  '1': 'vous a remarquée',
  '2': 'compte sur vous',
  '3': 'fait équipe avec vous',
};

export function trustVariableKey(characterId: string): string {
  return `trust_${characterId}`;
}

export function readLiveTrustCounts(
  variables: Record<string, number>,
  characterIds: string[],
): TrustPointsByCharacter {
  const counts: TrustPointsByCharacter = {};
  for (const characterId of characterIds) {
    const value = variables[trustVariableKey(characterId)] ?? 0;
    if (value > 0) counts[characterId] = value;
  }
  return counts;
}

export function resetLiveTrustCounts(
  variables: Record<string, number>,
  characterIds: string[],
): Record<string, number> {
  const next = { ...variables };
  for (const characterId of characterIds) {
    next[trustVariableKey(characterId)] = 0;
  }
  return next;
}

export function resolveTrustLabelTier(points: number): '1' | '2' | '3' {
  if (points <= 1) return '1';
  if (points === 2) return '2';
  return '3';
}

export function resolveTrustCranTier(points: number): 0 | 1 | 2 | 3 {
  if (points <= 0) return 0;
  if (points === 1) return 1;
  if (points === 2) return 2;
  return 3;
}

/**
 * Maps live trust points (1–3) or legacy percentage scores (10 / 20 / 30)
 * to the 0–3 cran shown on the ally tile.
 */
export function resolveDisplayCran(points: number): 0 | 1 | 2 | 3 {
  if (points <= 0) return 0;
  if (points <= 3) return resolveTrustCranTier(points);
  return Math.max(0, Math.min(3, Math.floor(points / 10))) as 0 | 1 | 2 | 3;
}

export const TRUST_LOSS_LABEL = 'vous lui avez fait confiance';

export function resolveTrustLabel(points: number, labels?: TrustLabels): string {
  const tier = resolveTrustLabelTier(points);
  return labels?.[tier] ?? DEFAULT_TRUST_LABELS[tier];
}

export function isAllyCharacter(character: CharacterPortrait): boolean {
  if (NON_ALLY_ID_RE.test(character.id)) return false;
  return !NON_ALLY_NAME_RE.test(character.name.trim());
}

export function splitCharacterDisplayName(name: string): { givenName: string; role: string } {
  const parts = name.split(/\s(?:[-–—]|·)\s/);
  if (parts.length >= 2) {
    return {
      givenName: (parts[0] ?? name).trim(),
      role: parts.slice(1).join(' — ').trim(),
    };
  }
  return { givenName: name.trim(), role: '' };
}

function frenchCount(n: number): string {
  return FRENCH_COUNTS[n] ?? String(n);
}

export function formatAllyTrustSubtitle(rows: AllyTrustDisplayRow[]): {
  trusted: string | null;
  waiting: string | null;
} {
  const trustedCount = rows.filter((row) => row.points >= 2).length;
  const waitingCount = rows.filter((row) => row.points <= 0).length;

  let trusted: string | null = null;
  if (trustedCount === 1) {
    trusted = 'Une personne compte sur vous.';
  } else if (trustedCount > 1) {
    trusted = `${frenchCount(trustedCount)} personnes comptent sur vous.`;
  }

  let waiting: string | null = null;
  if (waitingCount === 1) {
    waiting = 'Une autre attend encore.';
  } else if (waitingCount > 1 && waitingCount <= 5) {
    waiting = `${frenchCount(waitingCount)} autres attendent encore.`;
  }

  return { trusted, waiting };
}

export function loadTrustCampaignStore(scenarioId: string): TrustCampaignStore {
  try {
    const raw = localStorage.getItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`);
    if (!raw) return { byMission: {} };
    const parsed = JSON.parse(raw) as TrustCampaignStore;
    return { byMission: parsed.byMission ?? {} };
  } catch {
    return { byMission: {} };
  }
}

export function saveTrustCampaignStore(scenarioId: string, store: TrustCampaignStore): void {
  localStorage.setItem(`${CAMPAIGN_KEY_PREFIX}${scenarioId}`, JSON.stringify(store));
}

export function mergeCampaignTrustCounts(
  store: TrustCampaignStore,
  missions: Mission[],
  currentMissionId: string | undefined,
  currentMissionOrder: number,
  liveCounts: TrustPointsByCharacter,
): TrustPointsByCharacter {
  const merged: TrustPointsByCharacter = {};

  for (const mission of missions) {
    if (mission.order >= currentMissionOrder) continue;
    const stored = store.byMission[mission.id];
    if (!stored) continue;
    for (const [characterId, points] of Object.entries(stored)) {
      if (points <= 0) continue;
      merged[characterId] = (merged[characterId] ?? 0) + points;
    }
  }

  if (currentMissionId) {
    const currentMission = missions.find((mission) => mission.id === currentMissionId);
    if (currentMission && currentMission.order === currentMissionOrder) {
      for (const [characterId, points] of Object.entries(liveCounts)) {
        if (points <= 0) continue;
        merged[characterId] = (merged[characterId] ?? 0) + points;
      }
    }
  }

  return merged;
}

export function flushLiveMissionTrustToCampaign(
  scenarioId: string,
  missionId: string,
  liveCounts: TrustPointsByCharacter,
): TrustCampaignStore {
  const store = loadTrustCampaignStore(scenarioId);
  store.byMission[missionId] = { ...liveCounts };
  saveTrustCampaignStore(scenarioId, store);
  return store;
}

export function applyTrustGain(
  variables: Record<string, number>,
  characterId: string,
  points: number,
): Record<string, number> {
  if (points <= 0) return variables;
  const key = trustVariableKey(characterId);
  return {
    ...variables,
    [key]: (variables[key] ?? 0) + points,
  };
}

export function buildAllyTrustDisplayRows(options: {
  totals: TrustPointsByCharacter;
  characters: CharacterPortrait[];
  trustLabels?: TrustLabels;
  resolvePortrait?: (character: CharacterPortrait) => string | undefined;
  includeAllCharacters?: boolean;
}): AllyTrustDisplayRow[] {
  const { totals, characters, trustLabels, resolvePortrait, includeAllCharacters } = options;
  const source = includeAllCharacters
    ? characters.filter(isAllyCharacter)
    : characters.filter((character) => (totals[character.id] ?? 0) > 0);

  const rows = source
    .map((character) => {
      const points = totals[character.id] ?? 0;
      const { givenName, role } = splitCharacterDisplayName(character.name);
      const portraitImage = resolvePortrait
        ? resolvePortrait(character)
        : (character.icon ?? character.defaultImage);
      return {
        characterId: character.id,
        name: character.name,
        givenName,
        role,
        portraitImage,
        label: points <= 0 ? ZERO_TRUST_LABEL : resolveTrustLabel(points, trustLabels),
        points,
        cranTier: resolveTrustCranTier(points),
      };
    })
    .sort((a, b) => b.points - a.points || a.givenName.localeCompare(b.givenName, 'fr'));

  if (!includeAllCharacters) return rows;

  const withPoints = rows.filter((row) => row.points > 0);
  const zeros = rows.filter((row) => row.points <= 0);
  const fill = Math.max(0, 5 - withPoints.length);
  return [...withPoints, ...zeros.slice(0, fill)];
}
